import { foldedRanges, foldState } from '@codemirror/language';
import { RangeSet } from '@codemirror/state';
import type { EditorState, Extension } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  GutterMarker,
  gutterLineClass,
} from '@codemirror/view';
import type { DecorationSet } from '@codemirror/view';

const activeLineDecoration = Decoration.line({ class: 'cm-activeLine' });

class ActiveLineGutterMarker extends GutterMarker {
  override elementClass = 'cm-activeLineGutter';
}

const activeLineGutterMarker = new ActiveLineGutterMarker();

/**
 * Finds the opening line of the fold containing `lineStart`.
 *
 * @param state - The editor state.
 * @param folded - The folded ranges.
 * @param lineStart - The start offset of the cursor line.
 * @returns The folded block's opening line, or `lineStart` if it is not folded.
 */
function foldOpeningLine(
  state: EditorState,
  folded: DecorationSet,
  lineStart: number,
): number {
  let result = lineStart;

  folded.between(lineStart, lineStart, (from: number) => {
    if (from >= lineStart) {
      return;
    }
    const openingLine = state.doc.lineAt(from).from;
    if (openingLine < result) {
      result = openingLine;
    }
  });

  return result;
}

/**
 * Finds the opening lines of the blocks containing the cursors.
 * The content and gutter highlights share these offsets to stay aligned.
 *
 * @param state - The editor state.
 * @returns The sorted, unique start offsets of the active blocks.
 * @internal
 */
export function _activeLineStarts(state: EditorState): number[] {
  const folded = foldedRanges(state);
  const starts = new Set<number>();

  for (const range of state.selection.ranges) {
    let start = state.doc.lineAt(range.head).from;

    // Folds from `foldable` are always properly nested, so one pass already finds
    // the outermost opening line. This guards the unenforced case of a fold that
    // starts before another and ends inside it; it terminates because each pass
    // returns either the same offset or a strictly smaller one.
    let previous = -1;
    while (start !== previous) {
      previous = start;
      start = foldOpeningLine(state, folded, start);
    }

    starts.add(start);
  }

  return [...starts].sort((a, b) => a - b);
}

/**
 * Highlights the active line in the content and every gutter column, including
 * when the cursor is inside folded code.
 *
 * Replaces CodeMirror's separate active-line extensions, which can become
 * misaligned around folded code.
 *
 * @internal
 */
export const activeLineHighlight: Extension = [
  EditorView.decorations.compute(['selection', foldState], (state) =>
    Decoration.set(
      _activeLineStarts(state).map((start) =>
        activeLineDecoration.range(start),
      ),
    ),
  ),
  gutterLineClass.compute(['selection', foldState], (state) =>
    RangeSet.of(
      _activeLineStarts(state).map((start) =>
        activeLineGutterMarker.range(start),
      ),
    ),
  ),
];
